import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from './ui/button.js';
import { Card, CardContent } from './ui/card.js';

const NEULADEN_KEY = 'civiport.neugeladen';

/** Nach einem neuen Build fehlen die Dateien der alten Version: dann einmal neu laden. */
export function istVeralteteVersion(error: unknown): boolean {
  const text = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(text);
}

/**
 * Laedt die Seite neu, wenn die letzte Neuladung nicht gerade erst war.
 * Die Sperre verhindert eine Schleife, falls die Datei auch nach dem Neuladen fehlt.
 */
export function einmalNeuLaden(): boolean {
  try {
    const zuletzt = Number(sessionStorage.getItem(NEULADEN_KEY) ?? 0);
    if (Date.now() - zuletzt < 10_000) return false;
    sessionStorage.setItem(NEULADEN_KEY, String(Date.now()));
  } catch {
    // Ohne sessionStorage keine Sperre moeglich: lieber die Meldung zeigen.
    return false;
  }
  window.location.reload();
  return true;
}

interface Props {
  /** Wechselt der Schluessel (Pfad), verschwindet die Meldung wieder. */
  resetKey: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/** Faengt Fehler einer Seite ab, damit Navigation und Rahmen stehen bleiben. */
export class SeitenFehler extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (istVeralteteVersion(error) && einmalNeuLaden()) return;
    console.error('Seitenfehler', error, info.componentStack);
  }

  componentDidUpdate(prev: Props): void {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    const veraltet = istVeralteteVersion(error);
    return (
      <Card role="alert" className="border-destructive/40">
        <CardContent className="flex flex-col gap-3 p-6">
          <h2 className="text-lg font-semibold">
            {veraltet ? 'CiviPort wurde aktualisiert' : 'Diese Seite konnte nicht angezeigt werden'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {veraltet
              ? 'Die Seite gehört zu einer älteren Version. Bitte laden Sie neu.'
              : 'Ein unerwarteter Fehler ist aufgetreten. Laden Sie die Seite neu oder wechseln Sie zu einer anderen Seite.'}
          </p>
          <div>
            <Button onClick={() => window.location.reload()}>Neu laden</Button>
          </div>
        </CardContent>
      </Card>
    );
  }
}
